import { onMounted, onUnmounted, ref } from 'vue'
import type { Ref } from 'vue'

import { isScrollLocked } from '@/composables/useScrollLock'

interface ScrollHeader {
  /** Скроллим вниз за порогом — часть шапки пора спрятать */
  isHidden: Ref<boolean>
  /** Страница проскроллена — шапка «прилипла» и ей нужна тень */
  isStuck: Ref<boolean>
}

/**
 * У самого низа шапку не сворачиваем, а разворачиваем: свёрнутая укорачивает
 * страницу, браузер подтягивает скролл вверх, следующая дельта разворачивает
 * шапку обратно — и она начинает мигать. Зона чуть больше той высоты, на
 * которую шапка складывается.
 */
const BOTTOM_ZONE = 64

/**
 * Поведение сворачивающейся sticky-шапки: при скролле вниз дальше
 * `threshold` скрываем часть шапки, при скролле вверх — возвращаем.
 * Накопление дельты (8px) гасит дрожание на мелких колебаниях скролла.
 */
export function useScrollHeader(threshold = 120): ScrollHeader {
  const isHidden = ref(false)
  const isStuck = ref(false)

  let lastY = 0
  let acc = 0

  function onScroll(): void {
    /*
     * Под открытым шитом страница висит на `position: fixed`, и scrollY там
     * ноль — не «вернулись наверх», а «страница отложена». Без этой проверки
     * свёрнутый поиск разворачивался на открытии шита и сворачивался обратно
     * на закрытии. Позицию не трогаем: после снятия блокировки скролл
     * восстановится сам, дельта выйдет нулевой, и шапка останется как была.
     */
    if (isScrollLocked()) return

    /*
     * Кламп в границы документа: при резиновом отскоке (macOS/iOS) scrollY
     * выходит за пределы, и дельты пружины дают ложную смену направления.
     */
    const max = document.documentElement.scrollHeight - window.innerHeight
    const y = Math.min(Math.max(window.scrollY, 0), Math.max(max, 0))
    const delta = y - lastY
    lastY = y
    isStuck.value = y > 10

    /* Скроллить дальше некуда — просто показываем шапку и выходим из качелей */
    if (max - y <= BOTTOM_ZONE) {
      isHidden.value = false
      acc = 0
      return
    }

    if (delta === 0) return

    if (y <= threshold) {
      isHidden.value = false
      acc = 0
      return
    }
    acc = Math.sign(delta) === Math.sign(acc) ? acc + delta : delta
    if (acc > 8) {
      isHidden.value = true
    } else if (acc < -8) {
      isHidden.value = false
    }
  }

  /*
   * Страховочный поллинг: во время CSS-анимаций высоты шапки браузер
   * изредка теряет scroll-события — состояние догоняет позицию максимум
   * за 150мс, шапка не «залипает».
   */
  let pollId = 0

  onMounted(() => {
    lastY = window.scrollY
    isStuck.value = lastY > 10
    window.addEventListener('scroll', onScroll, { passive: true })
    pollId = window.setInterval(onScroll, 150)
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', onScroll)
    window.clearInterval(pollId)
  })

  return { isHidden, isStuck }
}
